import React, { useMemo } from "react";
import { useSiteContent } from "../i18n/SiteContent";

/**
 * Auto-sliding "Trusted by Clients & Consultants" logo strip.
 * CMS key: `clients.logos` (JSON: { heading, sub, logos: [{name, image_id?}] })
 *
 * If no image_id is provided, we render a clean wordmark styled tile — this
 * avoids visual gaps while brand assets are being collected.
 */
export default function ClientsMarquee() {
  const { pg } = useSiteContent();

  const defaultPayload = useMemo(() => ({
    heading: "Trusted by Clients & Consultants",
    sub: "Beyond distribution — we deliver transformation services.",
    logos: [
      { name: "Etisalat" },         { name: "Du Telecom" },
      { name: "Masdar" },           { name: "Emaar" },
      { name: "Azizi" },            { name: "DP World" },
      { name: "Alpha Data" },       { name: "SIBCA" },
      { name: "Trojan Holding" },   { name: "Tiger Contracting" },
      { name: "Verger Delporte" },  { name: "Telematics" },
      { name: "BMTS" },             { name: "We One" },
    ],
  }), []);

  const rawStr = pg("clients.logos", null);
  const payload = (() => {
    if (rawStr && typeof rawStr === "string") {
      try {
        const parsed = JSON.parse(rawStr);
        if (parsed && Array.isArray(parsed.logos)) return parsed;
      } catch { /* fallthrough */ }
    }
    if (rawStr && typeof rawStr === "object" && Array.isArray(rawStr.logos)) return rawStr;
    return defaultPayload;
  })();
  const logos = payload.logos || [];
  // Duplicate list for a seamless loop
  const loop = [...logos, ...logos];

  return (
    <section
      id="clients"
      data-testid="clients-marquee"
      className="relative overflow-hidden py-14 md:py-20 reveal"
      style={{ background: "#0F2A6E" }}
    >
      <div className="absolute inset-0 grid-bg opacity-10" />
      <div className="absolute -top-32 right-1/4 w-[520px] h-[520px] rounded-full blur-3xl"
           style={{ background: "rgba(27,111,224,0.18)" }} />

      <div className="relative max-w-7xl mx-auto px-4 lg:px-8">
        <div className="max-w-3xl">
          <div className="text-[11px] uppercase tracking-[0.22em] font-semibold mb-3 anim anim-up"
               style={{ color: "#8FB5FF" }}>
            {payload.sub || "Beyond distribution — we deliver transformation services."}
          </div>
          <h2 className="font-heading font-bold text-white tracking-tight text-4xl sm:text-5xl lg:text-[54px] leading-[1.05] anim anim-up"
              style={{ letterSpacing: "-0.025em" }}
              data-testid="clients-marquee-heading">
            {payload.heading || "Trusted by Clients & Consultants"}
          </h2>
        </div>

        {/* MARQUEE */}
        <div
          className="mt-10 md:mt-14 relative overflow-hidden py-2"
          style={{
            maskImage: "linear-gradient(90deg, transparent 0, #000 8%, #000 92%, transparent 100%)",
            WebkitMaskImage: "linear-gradient(90deg, transparent 0, #000 8%, #000 92%, transparent 100%)",
          }}
        >
          <div
            className="flex items-center gap-4 md:gap-6 marquee-track"
            style={{ animationDuration: `${Math.max(30, logos.length * 4)}s` }}
          >
            {loop.map((logo, i) => {
              const hasImg = !!(logo.logo_url || logo.image_id);
              return (
              <a
                key={i}
                href={logo.website || "#"}
                target={logo.website ? "_blank" : undefined}
                rel={logo.website ? "noreferrer" : undefined}
                data-testid={`client-logo-${i % logos.length}`}
                className="shrink-0 h-20 md:h-24 min-w-[180px] md:min-w-[220px] rounded-xl border border-white/10 bg-white hover:bg-white flex items-center justify-center px-6 transition"
              >
                {logo.image_id ? (
                  <ClientLogo id={logo.image_id} name={logo.name} />
                ) : logo.logo_url ? (
                  <img
                    src={logo.logo_url}
                    alt={logo.name}
                    loading="lazy"
                    className="max-h-12 md:max-h-14 max-w-[160px] object-contain"
                  />
                ) : null}
                {!hasImg && (
                  <span
                    className="font-heading font-bold text-[15px] md:text-[17px] tracking-tight text-center"
                    style={{ color: "#0F2A6E", letterSpacing: "-0.015em" }}
                  >
                    {logo.name}
                  </span>
                )}
              </a>
              );
            })}
          </div>
        </div>
      </div>

      <style>{`
        .marquee-track {
          width: max-content;
          animation-name: marquee-scroll;
          animation-timing-function: linear;
          animation-iteration-count: infinite;
        }
        .marquee-track:hover { animation-play-state: paused; }
        @keyframes marquee-scroll {
          from { transform: translateX(0); }
          to   { transform: translateX(-50%); }
        }
      `}</style>
    </section>
  );
}

function ClientLogo({ id, name }) {
  const [src, setSrc] = React.useState(null);
  React.useEffect(() => {
    if (!id) return;
    let c = false;
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/images/${id}`)
      .then((r) => r.json())
      .then((j) => !c && setSrc(j.data_url))
      .catch(() => {});
    return () => { c = true; };
  }, [id]);
  if (!src) return <span className="text-[#0F2A6E]/60 text-[13px] font-heading font-semibold">{name}</span>;
  return <img src={src} alt={name} loading="lazy" className="max-h-12 md:max-h-14 max-w-[160px] object-contain" />;
}
